/**
 * Admin Banner — compact one-line strip rendered just below the home
 * header for admin users only. Shows the same 4 counters as the admin
 * dashboard (vehicles, users, messages, calls) plus an "⚙️ פאנל אדמין"
 * button that opens the admin page in a full-screen modal.
 *
 * Lifecycle:
 *   - `mount()` inserts the strip into `#homeScreen` (right after .home-header)
 *   - `unmount()` removes the strip (called on logout / non-admin user)
 *   - Auto-refreshes stats every REFRESH_INTERVAL_MS (60s)
 *
 * Idempotent — calling `mount()` twice is a no-op.
 */

import { getStats } from './adminStats.service.js';
import { createLogger } from '../../core/logger.js';

const log = createLogger('admin-banner');

const BANNER_ID = 'adminBanner';
const REFRESH_INTERVAL_MS = 60 * 1000;

/** @type {number | null} */
let refreshTimer = null;

/**
 * Insert the banner strip + start the refresh loop.
 *
 * Idempotent. If the home-header isn't in the DOM yet (e.g. the home
 * screen is still hidden behind the login flow when auth:ready fires),
 * we poll briefly until it appears — up to ~5 seconds — so the admin
 * doesn't have to refresh the page to see the banner.
 */
export function mount() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(BANNER_ID)) return;  // idempotent

  const attemptInsert = () => {
    if (document.getElementById(BANNER_ID)) return true;
    const homeScreen = document.getElementById('homeScreen');
    const header = homeScreen?.querySelector('.home-header');
    if (!header) return false;

    const banner = document.createElement('div');
    banner.id = BANNER_ID;
    banner.className = 'admin-banner';
    banner.innerHTML = renderShell();
    header.insertAdjacentElement('afterend', banner);

    banner.querySelector('[data-action="open-admin"]')?.addEventListener('click', onOpenAdmin);
    banner.querySelector('[data-action="refresh"]')?.addEventListener('click', () => void load(true));

    void load(false);
    refreshTimer = window.setInterval(() => void load(false), REFRESH_INTERVAL_MS);
    log.info('mounted');
    return true;
  };

  if (attemptInsert()) return;

  // Home header not ready yet — poll every 250ms for up to 5 seconds.
  log.info('home-header not ready — polling…');
  let tries = 0;
  const maxTries = 20;
  const interval = window.setInterval(() => {
    tries++;
    if (attemptInsert() || tries >= maxTries) {
      clearInterval(interval);
      if (tries >= maxTries && !document.getElementById(BANNER_ID)) {
        log.warn('gave up — home-header never appeared');
      }
    }
  }, 250);
}

/** Remove the banner + stop the refresh loop. */
export function unmount() {
  if (refreshTimer != null) {
    clearInterval(refreshTimer);
    refreshTimer = null;
  }
  document.getElementById(BANNER_ID)?.remove();
  log.info('unmounted');
}

function renderShell() {
  // Each stat is a colored "pill" — translucent colored background +
  // matching colored icon, white bold number. Keeps the banner on a
  // single row with strong visual differentiation between metrics.
  return `
    <div class="admin-banner-stats" id="adminBannerStats">
      <span class="admin-banner-stat" data-stat-color="vehicles"
            title="רכבים פעילים — לפי plateIndex (לא כולל רכבים יתומים)">
        <span class="admin-banner-stat-icon">🚗</span>
        <span class="admin-banner-num" data-stat="vehicles">…</span>
      </span>
      <span class="admin-banner-stat" data-stat-color="users"
            title="משתמשים פעילים — לפי phoneIndex (לא כולל uid כפולים או חשבונות ללא טלפון)">
        <span class="admin-banner-stat-icon">👥</span>
        <span class="admin-banner-num" data-stat="users">…</span>
      </span>
      <span class="admin-banner-stat" data-stat-color="messages"
            title="סך כל ההודעות במערכת">
        <span class="admin-banner-stat-icon">💬</span>
        <span class="admin-banner-num" data-stat="messages">…</span>
      </span>
      <span class="admin-banner-stat" data-stat-color="calls"
            title="סך כל השיחות במערכת">
        <span class="admin-banner-stat-icon">📞</span>
        <span class="admin-banner-num" data-stat="calls">…</span>
      </span>
    </div>
    <button type="button" class="admin-banner-refresh" data-action="refresh" aria-label="רענן" title="רענן סטטיסטיקות">🔄</button>
    <button type="button" class="admin-banner-open" data-action="open-admin">
      <span class="admin-banner-open-icon">⚙️</span>
      <span class="admin-banner-open-text">פאנל אדמין</span>
    </button>
  `;
}

/**
 * Format a count for compact display in the banner.
 *   0–999       → "847"
 *   1k–9,999    → "1,234"   (comma-separated)
 *   10k–999,999 → "12.3K"   (1 decimal, trims ".0")
 *   1M+         → "1.2M"
 * @param {number} n
 * @returns {string}
 */
function formatNum(n) {
  if (!Number.isFinite(n) || n < 0) return '0';
  if (n < 1000) return String(n);
  if (n < 10_000) return n.toLocaleString('he-IL');
  if (n < 1_000_000) return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
  return (n / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
}

async function load(force) {
  const banner = document.getElementById(BANNER_ID);
  if (!banner) return;
  banner.classList.toggle('is-loading', true);
  try {
    const stats = await getStats(!!force);
    setNum(banner, 'vehicles', stats.vehicles);
    setNum(banner, 'users',    stats.users);
    setNum(banner, 'messages', stats.messages);
    setNum(banner, 'calls',    stats.calls);
  } catch (err) {
    log.warn('load failed:', err);
  } finally {
    banner.classList.toggle('is-loading', false);
  }
}

function setNum(banner, key, value) {
  const el = banner.querySelector(`[data-stat="${key}"]`);
  if (el) el.textContent = formatNum(value);
}

function onOpenAdmin() {
  // Lazy-import to avoid circular module load (frame.view → bootstrap → banner).
  void import('./frame.view.js').then((mod) => mod.open());
}
